import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast
} from '@chakra-ui/react'
import { FaCloudUploadAlt, FaDownload, FaEye, FaTrash } from 'react-icons/fa'
import { useDropzone } from 'react-dropzone'
import {
  bulkDeleteSupportingDocument,
  bulkDownloadSupportingDocument,
  deleteSupportingDocument,
  downloadSupportingDocument,
  getSupportingDocuments,
  uploadSupportingDocument,
  viewSupportingDocument
} from '../../api/supportingDocumentsApi'
import { LABEL, MESSAGE } from '../../../../constants'

const SupportingDocumentsModal = ({ documentId, isOpen, onClose, user }) => {
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(false)
  const [files, setFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [previewDoc, setPreviewDoc] = useState(null)
  const toast = useToast()

  useEffect(() => {
    if (isOpen && documentId) {
      fetchDocuments()
    }
  }, [isOpen, documentId])

  const [pdfUrl, setPdfUrl] = useState(null)

  useEffect(() => {
    const fetchPreview = async () => {
      if (previewDoc) {
        try {
          const url = await viewSupportingDocument(documentId, previewDoc.id)
          setPdfUrl(url)
        } catch (err) {
          console.error('Error fetching preview', err)
        }
      } else {
        setPdfUrl(null)
      }
    }

    fetchPreview()
  }, [previewDoc, documentId])

  const fetchDocuments = async () => {
    setLoading(true)
    try {
      const data = await getSupportingDocuments(documentId)
      setDocuments(data)
    } catch (error) {
      console.error(MESSAGE.SUPPORTING_DOCUMENTS_ERRORS.get, error)
    } finally {
      setLoading(false)
    }
  }

  const handleFileUpload = async () => {
    if (!files.length) return
    setUploading(true)
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    try {
      await uploadSupportingDocument(documentId, formData)
      setFiles([])
      fetchDocuments()
      toast({
        title: 'Fișiere încărcate!',
        description: `${files.length} fișiere au fost încărcate cu succes.`,
        status: 'success',
        duration: 3000,
        isClosable: true
      })
    } catch (error) {
      console.error(MESSAGE.SUPPORTING_DOCUMENTS_ERRORS.upload, error)
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = async (documentId, downloadID, fileName) => {
    try {
      await downloadSupportingDocument(documentId, downloadID, fileName)
    } catch (error) {
      console.error(
        MESSAGE.SUPPORTING_DOCUMENTS_ERRORS.downloadDocuments,
        error
      )
    }
  }

  const handleBulkDownload = async () => {
    try {
      await bulkDownloadSupportingDocument(documentId)
    } catch (error) {
      console.error(MESSAGE.SUPPORTING_DOCUMENTS_ERRORS.downloadZip, error)
    }
  }

  const handleDelete = async (documentId, downloadID) => {
    try {
      await deleteSupportingDocument(documentId, downloadID)
      fetchDocuments()
    } catch (error) {
      console.error(MESSAGE.SUPPORTING_DOCUMENTS_ERRORS.delete, error)
    }
  }

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteSupportingDocument(documentId)
      fetchDocuments()
    } catch (error) {
      console.error(MESSAGE.SUPPORTING_DOCUMENTS_ERRORS.delete, error)
    }
  }

  const onDrop = (acceptedFiles, rejectedFiles) => {
    const totalSize = acceptedFiles.reduce((sum, file) => sum + file.size, 0)

    if (rejectedFiles.length > 0 || totalSize > 25 * 1024 * 1024) {
      toast({
        title: MESSAGE.FILE_SIZE_ERROR.title,
        description: MESSAGE.FILE_SIZE_ERROR.description,
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }

    setFiles(acceptedFiles)
  }

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    accept: 'image/*,application/pdf,.docx,.xlsx',
    maxSize: 25 * 1024 * 1024
  })

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxHeight="80vh" overflow="hidden">
          <ModalHeader>Documente Justificative</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" p={4}>
            {!user.user.isGuest && (
              <>
                <Box
                  {...getRootProps()}
                  p={6}
                  border="2px dashed gray"
                  borderRadius="md"
                  textAlign="center"
                  cursor="pointer"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  background="gray.50"
                  _hover={{ background: 'gray.100' }}
                >
                  <input {...getInputProps()} />
                  <Icon
                    color="gray.500"
                    as={FaCloudUploadAlt}
                    boxSize={12}
                    mb={2}
                  />
                  <Text fontSize="lg" fontWeight="bold">
                    Trage fișiere aici sau apasă pentru a selecta
                  </Text>
                  {files.length > 0 && (
                    <Text mt={2} fontWeight="bold" color="blue.500">
                      {LABEL.SUPPORTING_DOCUMENTS.selectedFiles(files)}
                    </Text>
                  )}
                </Box>
                </>
            )}

                {loading ? (
                  <Flex align="center" justify="center" mt={4}>
                    <Spinner size="lg" />
                    <Text ml={4}>{LABEL.LOADING.label}</Text>
                  </Flex>
                ) : documents.length > 0 ? (
                  <Box overflowY="auto" maxHeight="400px" mt={4}>
                    <Table variant="striped" size="sm">
                      <Thead>
                        <Tr>
                          <Th>{LABEL.SUPPORTING_DOCUMENTS.index}</Th>
                          <Th>{LABEL.SUPPORTING_DOCUMENTS.name}</Th>
                          <Th>{LABEL.SUPPORTING_DOCUMENTS.fileSize}</Th>
                          <Th textAlign="right">Acțiuni</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {documents.map((doc, index) => (
                          <Tr key={doc.id}>
                            <Td>{index + 1}</Td>
                            <Td>{doc.fileName}</Td>
                            <Td>{doc.fileSize.toFixed(3)}</Td>
                            <Td>
                              <Flex gap={2} justifyContent="flex-end">
                                <Button
                                  colorScheme="green"
                                  size="sm"
                                  onClick={() => setPreviewDoc(doc)}
                                >
                                  <FaEye />
                                </Button>
                                <Button
                                  colorScheme="blue"
                                  size="sm"
                                  onClick={() =>
                                    handleDownload(
                                      documentId,
                                      doc.id,
                                      doc.fileName
                                    )
                                  }
                                >
                                  <FaDownload />
                                </Button>
                                {!user.user.isGuest && (<Button
                                  colorScheme="red"
                                  size="sm"
                                  onClick={() =>
                                    handleDelete(documentId, doc.id)
                                  }
                                >
                                  <FaTrash />
                                </Button>)}
                              </Flex>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                ) : (
                  <Text width="100%" textAlign="center" mt={4}>
                    Nu există documente justificative.
                  </Text>
                )}
          </ModalBody>

          <ModalFooter>
                {documents.length > 0 && !user.user.isGuest &&(
                  <Button
                    colorScheme="red"
                    onClick={handleBulkDelete}
                    leftIcon={<FaTrash />}
                  >
                    {LABEL.SUPPORTING_DOCUMENTS_BUTTONS.deleteAll}
                  </Button>
                )}
                <Flex ml="auto">
                  {documents.length > 0 && (
                    <Button
                      colorScheme="blue"
                      onClick={handleBulkDownload}
                      leftIcon={<FaDownload />}
                      ml={4}
                    >
                      {LABEL.SUPPORTING_DOCUMENTS_BUTTONS.zipDownload}
                    </Button>
                  )}

                  {!user.user.isGuest && (<Button
                    colorScheme="brand"
                    onClick={handleFileUpload}
                    ml={4}
                    leftIcon={<FaCloudUploadAlt />}
                    isLoading={uploading}
                    isDisabled={files.length === 0}
                  >
                    Încarcă
                  </Button>)}
                </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        size="6xl"
      >
        <ModalOverlay />
        <ModalContent height="90vh">
          <ModalHeader>{previewDoc?.fileName}</ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0}>
            {pdfUrl && previewDoc?.fileName?.endsWith('.pdf') ? (
              <iframe
                src={pdfUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              />
            ) : (
              <Text p={4}>Preview disponibil doar pentru PDF-uri.</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

export default SupportingDocumentsModal
